import React, { useState } from 'react';
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Form, Row, Col, Table, Button, Badge, Modal, Nav, Tab, Accordion } from "react-bootstrap";


import PageTitle from "@/common/PageTitle/PageTitle";
import styles from "./llt.module.scss";

interface LLTLocation {
  id: string;
  locationNumber: string;
  responsibleForSubscription: string;
  storeNumber: string;
  companyName: string;
  companyAddress: string;
  timeZone: string;
  musicZone1: string;
  musicZone2: string;
  musicZone3: string;
  hoursOfOperation: string;
  wakeSleepSetAt: string;
  pin: string;
  kkAppVersion: string;
  kkAssistantAppVersion: string;
  kkPlayerSerialNumber: string;
  kkPlayerTested: string;
  itPersonName: string;
  itPersonNumber: string;
  itPersonEmail: string;
  contract: string;
  dateDevicesOrdered: string;
  trialPeriodStartDate: string;
  billingCycleDate: string;
  musicPlanStartDate: string;
  devicePaymentProcessed: boolean;
  musicPlanPaymentProcessed: boolean;
  shippingCompanyName: string;
  shippingAddress: string;
  responsiblePartyName: string;
  numberOfDevices: number;
  accessories: string;
  shippingPrice: string;
  trackingNumber: string;
  status: 'pending' | 'active' | 'completed' | 'inactive';
  clientId: string;
  brandId: string;
  createdAt: string;
  updatedAt: string;
}

interface Client {
  id: string;
  name: string;
  brands: Brand[];
}

interface Brand {
  id: string;
  name: string;
  locations: LLTLocation[];
}

const LLT: React.FC = () => {
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LLTLocation | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data for clients and their brands
  const clients: Client[] = [
    {
      id: 'CLI-001',
      name: 'Johnson Corp',
      brands: [
        {
          id: 'BRAND-001',
          name: 'Summit Diners',
          locations: [
            {
              id: 'LOC-001',
              locationNumber: 'BK-001',
              responsibleForSubscription: 'Johnson Corp',
              storeNumber: '1345',
              companyName: 'Summit Diners Downtown',
              companyAddress: '123 Main Street, Houston, TX 77001',
              timeZone: 'CST',
              musicZone1: 'Dining Area',
              musicZone2: 'Drive-Thru',
              musicZone3: 'Kitchen',
              hoursOfOperation: 'M-F 6a-11p, Sat-Sun 7a-12a',
              wakeSleepSetAt: '6:00 AM / 11:00 PM',
              pin: '1234',
              kkAppVersion: '2.1.0',
              kkAssistantAppVersion: '1.5.2',
              kkPlayerSerialNumber: 'KK-2024-001234',
              kkPlayerTested: '2024-01-15',
              itPersonName: 'John Smith',
              itPersonNumber: '555-123-4567',
              itPersonEmail: 'john.smith@summitdiners.example.com',
              contract: 'CON-2024-001',
              dateDevicesOrdered: '2024-01-10',
              trialPeriodStartDate: '2024-01-20',
              billingCycleDate: '2024-02-01',
              musicPlanStartDate: '2024-02-01',
              devicePaymentProcessed: true,
              musicPlanPaymentProcessed: true,
              shippingCompanyName: 'Summit Diners Downtown',
              shippingAddress: '123 Main Street, Houston, TX 77001',
              responsiblePartyName: 'John Smith',
              numberOfDevices: 2,
              accessories: 'Mounting brackets, cables',
              shippingPrice: '$45.00',
              trackingNumber: '1Z999AA1234567890',
              status: 'active',
              clientId: 'CLI-001',
              brandId: 'BRAND-001',
              createdAt: '2024-01-10',
              updatedAt: '2024-01-15'
            }
          ]
        }
      ]
    },
    {
      id: 'CLI-002',
      name: 'John Doe Company',
      brands: [
        {
          id: 'BRAND-002',
          name: 'Harbor Foods',
          locations: [
            {
              id: 'LOC-002',
              locationNumber: 'MD-001',
              responsibleForSubscription: 'John Doe Company',
              storeNumber: '1409',
              companyName: 'Harbor Foods Uptown',
              companyAddress: '456 Oak Avenue, Austin, TX 73301',
              timeZone: 'CST',
              musicZone1: 'Lobby',
              musicZone2: 'Play Area',
              musicZone3: 'Drive-Thru',
              hoursOfOperation: '24/7',
              wakeSleepSetAt: 'Always On',
              pin: '5678',
              kkAppVersion: '2.1.0',
              kkAssistantAppVersion: '1.5.2',
              kkPlayerSerialNumber: 'KK-2024-001235',
              kkPlayerTested: '2024-01-18',
              itPersonName: 'Mary Johnson',
              itPersonNumber: '555-987-6543',
              itPersonEmail: 'mary.johnson@harborfoods.example.com',
              contract: 'CON-2024-002',
              dateDevicesOrdered: '2024-01-12',
              trialPeriodStartDate: '',
              billingCycleDate: '2024-02-15',
              musicPlanStartDate: '2024-01-25',
              devicePaymentProcessed: true,
              musicPlanPaymentProcessed: false,
              shippingCompanyName: 'Harbor Foods Uptown',
              shippingAddress: '456 Oak Avenue, Austin, TX 73301',
              responsiblePartyName: 'Mary Johnson',
              numberOfDevices: 3,
              accessories: 'Mounting brackets, cables, speakers',
              shippingPrice: '$65.00',
              trackingNumber: '1Z999AA1234567891',
              status: 'active',
              clientId: 'CLI-002',
              brandId: 'BRAND-002',
              createdAt: '2024-01-12',
              updatedAt: '2024-01-18'
            }
          ]
        },
        {
          id: 'BRAND-003',
          name: 'Bluebird Diner',
          locations: []
        }
      ]
    }
  ];

  // Get selected client data
  const selectedClientData = clients.find(client => client.id === selectedClient);
  const selectedBrandData = selectedClientData?.brands.find(brand => brand.id === selectedBrand);

  // Filter locations based on search term
  const filteredLocations = selectedBrandData?.locations.filter(location =>
    location.locationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.companyAddress.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleAddLocation = () => {
    setSelectedLocation(null);
    setShowAddLocationModal(true);
  };

  const handleEditLocation = (location: LLTLocation) => {
    setSelectedLocation(location);
    setShowEditLocationModal(true);
  };

  const handleSaveLocation = (locationData: Partial<LLTLocation>) => {
    if (selectedLocation) {
      // Update existing location
      console.log('Updating location:', { ...selectedLocation, ...locationData });
    } else {
      // Add new location
      console.log('Adding new location:', locationData);
    }
    setShowAddLocationModal(false);
    setShowEditLocationModal(false);
    setSelectedLocation(null);
  };

  const getStatusBadge = (status: string) => {
    const badgeColors = {
      'pending': 'warning',
      'active': 'success',
      'completed': 'info',
      'inactive': 'secondary'
    };
    return <Badge bg={badgeColors[status as keyof typeof badgeColors] || 'secondary'}>{status}</Badge>;
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Locations Logistic Template (LLT)" />
          </Card.Title>
          
          {/* Client and Brand Selection */}
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Select Client</Form.Label>
                <Form.Select
                  value={selectedClient}
                  onChange={(e) => {
                    setSelectedClient(e.target.value);
                    setSelectedBrand('');
                  }}
                >
                  <option value="">Choose a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Select Brand</Form.Label>
                <Form.Select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  disabled={!selectedClient}
                >
                  <option value="">Choose a brand...</option>
                  {selectedClientData?.brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {selectedClient && selectedBrand && (
            <>
              {/* Search and Actions */}
              <Row className="mb-3">
                <Col md={8}>
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="Search locations by number, name, or address..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={styles.searchInput}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="text-end">
                  <Button 
                    variant="primary"
                    onClick={handleAddLocation}
                    className={styles.addButton}
                  >
                    Add Location
                  </Button>
                </Col>
              </Row>

              {/* Tabs for different views */}
              <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')}>
                <Nav variant="tabs" className="mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="overview">Overview</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="details">Detailed View</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="shipping">Shipping Info</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="billing">Billing & Payments</Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  {/* Overview Tab */}
                  <Tab.Pane eventKey="overview">
                    <div className={styles.overviewGrid}>
                      {filteredLocations.map(location => (
                        <Card key={location.id} className={styles.locationCard}>
                          <Card.Header className={styles.cardHeader}>
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0">{location.locationNumber}</h6>
                              {getStatusBadge(location.status)}
                            </div>
                          </Card.Header>
                          <Card.Body>
                            <h6>{location.companyName}</h6>
                            <p className="text-muted mb-2">{location.companyAddress}</p>
                            <div className={styles.locationDetails}>
                              <div><strong>Store #:</strong> {location.storeNumber}</div>
                              <div><strong>Time Zone:</strong> {location.timeZone}</div>
                              <div><strong>Devices:</strong> {location.numberOfDevices}</div>
                              <div><strong>Hours:</strong> {location.hoursOfOperation}</div>
                            </div>
                            <div className="mt-3">
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => handleEditLocation(location)}
                              >
                                Edit
                              </Button>
                              <Button 
                                variant="outline-info" 
                                size="sm"
                                className="ms-2"
                                onClick={() => setActiveTab('details')}
                              >
                                View Details
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </Tab.Pane>

                  {/* Detailed View Tab */}
                  <Tab.Pane eventKey="details">
                    <Table hover responsive className="legacy-row-height-table">
                      <thead>
                        <tr>
                          <th>Location #</th>
                          <th>Company Name</th>
                          <th>Address</th>
                          <th>Time Zone</th>
                          <th>Music Zones</th>
                          <th>Hours</th>
                          <th>IT Contact</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLocations.map(location => (
                          <tr key={location.id}>
                            <td>{location.locationNumber}</td>
                            <td>{location.companyName}</td>
                            <td>{location.companyAddress}</td>
                            <td>{location.timeZone}</td>
                            <td>
                              {location.musicZone1}, {location.musicZone2}, {location.musicZone3}
                            </td>
                            <td>{location.hoursOfOperation}</td>
                            <td>
                              {location.itPersonName}<br />
                              <small className="text-muted">{location.itPersonNumber}</small>
                            </td>
                            <td>{getStatusBadge(location.status)}</td>
                            <td>
                              <Button 
                                variant="link" 
                                size="sm" 
                                className={styles.actionLink}
                                onClick={() => handleEditLocation(location)}
                              >
                                ✏️
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Tab.Pane>

                  {/* Shipping Info Tab */}
                  <Tab.Pane eventKey="shipping">
                    <Table hover responsive className="legacy-row-height-table">
                      <thead>
                        <tr>
                          <th>Location #</th>
                          <th>Company Name</th>
                          <th>Shipping Address</th>
                          <th>Responsible Party</th>
                          <th>Devices</th>
                          <th>Accessories</th>
                          <th>Shipping Price</th>
                          <th>Tracking #</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLocations.map(location => (
                          <tr key={location.id}>
                            <td>{location.locationNumber}</td>
                            <td>{location.shippingCompanyName}</td>
                            <td>{location.shippingAddress}</td>
                            <td>{location.responsiblePartyName}</td>
                            <td>{location.numberOfDevices}</td>
                            <td>{location.accessories}</td>
                            <td>{location.shippingPrice}</td>
                            <td>{location.trackingNumber || 'Pending'}</td>
                            <td>
                              <Button 
                                variant="link" 
                                size="sm" 
                                className={styles.actionLink}
                                onClick={() => handleEditLocation(location)}
                              >
                                ✏️
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Tab.Pane>

                  {/* Billing & Payments Tab */}
                  <Tab.Pane eventKey="billing">
                    <Table hover responsive className="legacy-row-height-table">
                      <thead>
                        <tr>
                          <th>Location #</th>
                          <th>Contract</th>
                          <th>Device Order Date</th>
                          <th>Trial Start</th>
                          <th>Music Plan Start</th>
                          <th>Billing Cycle</th>
                          <th>Device Payment</th>
                          <th>Music Payment</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLocations.map(location => (
                          <tr key={location.id}>
                            <td>{location.locationNumber}</td>
                            <td>{location.contract}</td>
                            <td>{location.dateDevicesOrdered}</td>
                            <td>{location.trialPeriodStartDate || 'N/A'}</td>
                            <td>{location.musicPlanStartDate}</td>
                            <td>{location.billingCycleDate}</td>
                            <td>
                              <Badge bg={location.devicePaymentProcessed ? 'success' : 'warning'}>
                                {location.devicePaymentProcessed ? 'Paid' : 'Pending'}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={location.musicPlanPaymentProcessed ? 'success' : 'warning'}>
                                {location.musicPlanPaymentProcessed ? 'Paid' : 'Pending'}
                              </Badge>
                            </td>
                            <td>
                              <Button 
                                variant="link" 
                                size="sm" 
                                className={styles.actionLink}
                                onClick={() => handleEditLocation(location)}
                              >
                                ✏️
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Location Modal */}
      <Modal 
        show={showAddLocationModal || showEditLocationModal} 
        onHide={() => {
          setShowAddLocationModal(false);
          setShowEditLocationModal(false);
          setSelectedLocation(null);
        }}
        size="xl"
        className={styles.lltModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedLocation ? 'Edit Location' : 'Add New Location'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LLTForm 
            location={selectedLocation}
            onSave={handleSaveLocation}
            onCancel={() => {
              setShowAddLocationModal(false);
              setShowEditLocationModal(false);
              setSelectedLocation(null);
            }}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

// LLT Form Component
interface LLTFormProps {
  location: LLTLocation | null;
  onSave: (data: Partial<LLTLocation>) => void;
  onCancel: () => void;
}

const LLTForm: React.FC<LLTFormProps> = ({ location, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Partial<LLTLocation>>(
    location || {
      locationNumber: '',
      responsibleForSubscription: '',
      storeNumber: '',
      companyName: '',
      companyAddress: '',
      timeZone: '',
      musicZone1: '',
      musicZone2: '',
      musicZone3: '',
      hoursOfOperation: '',
      wakeSleepSetAt: '',
      pin: '',
      kkAppVersion: '2.1.0',
      kkAssistantAppVersion: '1.5.2',
      kkPlayerSerialNumber: '',
      kkPlayerTested: '',
      itPersonName: '',
      itPersonNumber: '',
      itPersonEmail: '',
      contract: '',
      dateDevicesOrdered: '',
      trialPeriodStartDate: '',
      billingCycleDate: '',
      musicPlanStartDate: '',
      devicePaymentProcessed: false,
      musicPlanPaymentProcessed: false,
      shippingCompanyName: '',
      shippingAddress: '',
      responsiblePartyName: '',
      numberOfDevices: 1,
      accessories: '',
      shippingPrice: '',
      trackingNumber: '',
      status: 'pending'
    }
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Accordion defaultActiveKey="basic-info">
        {/* Basic Information */}
        <Accordion.Item eventKey="basic-info">
          <Accordion.Header>Basic Information</Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Location #</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.locationNumber}
                    onChange={(e) => handleInputChange('locationNumber', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Store #</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.storeNumber}
                    onChange={(e) => handleInputChange('storeNumber', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Responsible for Subscription</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.responsibleForSubscription}
                    onChange={(e) => handleInputChange('responsibleForSubscription', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Time Zone</Form.Label>
                  <Form.Select
                    value={formData.timeZone}
                    onChange={(e) => handleInputChange('timeZone', e.target.value)}
                    required
                  >
                    <option value="">Select Time Zone</option>
                    <option value="EST">Eastern (EST)</option>
                    <option value="CST">Central (CST)</option>
                    <option value="MST">Mountain (MST)</option>
                    <option value="PST">Pacific (PST)</option>
                    <option value="AKST">Alaska (AKST)</option>
                    <option value="HST">Hawaii (HST)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.companyAddress}
                    onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        {/* Music Zones & Operations */}
        <Accordion.Item eventKey="music-operations">
          <Accordion.Header>Music Zones & Operations</Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Music Zone 1</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.musicZone1}
                    onChange={(e) => handleInputChange('musicZone1', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Music Zone 2</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.musicZone2}
                    onChange={(e) => handleInputChange('musicZone2', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Music Zone 3</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.musicZone3}
                    onChange={(e) => handleInputChange('musicZone3', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Hours of Operation</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.hoursOfOperation}
                    onChange={(e) => handleInputChange('hoursOfOperation', e.target.value)}
                    placeholder="e.g., M-F 6a-11p, Sat-Sun 7a-12a"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Wake & Sleep Set At</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.wakeSleepSetAt}
                    onChange={(e) => handleInputChange('wakeSleepSetAt', e.target.value)}
                    placeholder="e.g., 6:00 AM / 11:00 PM"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        {/* Device Information */}
        <Accordion.Item eventKey="device-info">
          <Accordion.Header>Device Information</Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>PIN</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.pin}
                    onChange={(e) => handleInputChange('pin', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>KK App Version</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.kkAppVersion}
                    onChange={(e) => handleInputChange('kkAppVersion', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>KK Assistant App Version</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.kkAssistantAppVersion}
                    onChange={(e) => handleInputChange('kkAssistantAppVersion', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>KK Player Serial Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.kkPlayerSerialNumber}
                    onChange={(e) => handleInputChange('kkPlayerSerialNumber', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>KK Player Tested Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.kkPlayerTested}
                    onChange={(e) => handleInputChange('kkPlayerTested', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        {/* IT Contact */}
        <Accordion.Item eventKey="it-contact">
          <Accordion.Header>IT Contact Information</Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>IT Person Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.itPersonName}
                    onChange={(e) => handleInputChange('itPersonName', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>IT Person Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.itPersonNumber}
                    onChange={(e) => handleInputChange('itPersonNumber', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>IT Person Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.itPersonEmail}
                    onChange={(e) => handleInputChange('itPersonEmail', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        {/* Shipping Information */}
        <Accordion.Item eventKey="shipping-info">
          <Accordion.Header>Shipping Information</Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Shipping Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.shippingCompanyName}
                    onChange={(e) => handleInputChange('shippingCompanyName', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Responsible Party Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.responsiblePartyName}
                    onChange={(e) => handleInputChange('responsiblePartyName', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Shipping Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.shippingAddress}
                    onChange={(e) => handleInputChange('shippingAddress', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Devices</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.numberOfDevices}
                    onChange={(e) => handleInputChange('numberOfDevices', parseInt(e.target.value))}
                    min="1"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Accessories</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.accessories}
                    onChange={(e) => handleInputChange('accessories', e.target.value)}
                    placeholder="e.g., Mounting brackets, cables"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Shipping Price</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.shippingPrice}
                    onChange={(e) => handleInputChange('shippingPrice', e.target.value)}
                    placeholder="e.g., $45.00"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tracking Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.trackingNumber}
                    onChange={(e) => handleInputChange('trackingNumber', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        {/* Billing & Contracts */}
        <Accordion.Item eventKey="billing-contracts">
          <Accordion.Header>Billing & Contracts</Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contract</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.contract}
                    onChange={(e) => handleInputChange('contract', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date Devices Ordered</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.dateDevicesOrdered}
                    onChange={(e) => handleInputChange('dateDevicesOrdered', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Trial Period Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.trialPeriodStartDate}
                    onChange={(e) => handleInputChange('trialPeriodStartDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Music Plan Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.musicPlanStartDate}
                    onChange={(e) => handleInputChange('musicPlanStartDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Cycle Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.billingCycleDate}
                    onChange={(e) => handleInputChange('billingCycleDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Device Payment Processed"
                    checked={formData.devicePaymentProcessed}
                    onChange={(e) => handleInputChange('devicePaymentProcessed', e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Music Plan Payment Processed"
                    checked={formData.musicPlanPaymentProcessed}
                    onChange={(e) => handleInputChange('musicPlanPaymentProcessed', e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <div className="mt-4 text-end">
        <Button variant="secondary" onClick={onCancel} className="me-2">
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          {location ? 'Update Location' : 'Add Location'}
        </Button>
      </div>
    </Form>
  );
};

export default withAuthenticationRequired(LLT);
